/**
 * useActiveTeam (pirx-z0v4g.6).
 *
 * Reactive access to the active-team selection, reconciled against the
 * authoritative profile. Two responsibilities:
 *
 * 1. Expose the current active team id (or `null` for personal scope) and a
 *    setter, re-rendering subscribers when it changes (`useSyncExternalStore`).
 * 2. Self-heal: if the persisted active team is no longer one the user belongs
 *    to (they were removed, or localStorage carries a stale id from another
 *    account), reset to personal. Without this, every request would send an
 *    `X-Team-Id` the backend rejects with 403 — locking the user out.
 */

import { useEffect, useSyncExternalStore } from "react";
import type { UserProfile } from "@/features/auth/contexts/auth/context";
import {
  getActiveTeamId,
  setActiveTeamId,
  subscribeActiveTeam,
} from "@/services/teams/active-team";

export type ActiveTeam = {
  /** Teams the user belongs to (from the authoritative profile). */
  teams: UserProfile["teams"];
  /** Current active team id, or `null` for personal scope. */
  activeTeamId: string | null;
  /** The active team object, or `null` when in personal scope. */
  activeTeam: UserProfile["teams"][number] | null;
  /** Select a team (or `null` for personal scope). */
  setActiveTeam: (teamId: string | null) => void;
};

export function useActiveTeam(profile: UserProfile | null): ActiveTeam {
  const teams = profile?.teams ?? [];
  const storedId = useSyncExternalStore(
    subscribeActiveTeam,
    getActiveTeamId,
    getActiveTeamId,
  );

  // Reconcile: drop a selection the user no longer belongs to.
  const isMember = storedId !== null && teams.some((t) => t.id === storedId);
  useEffect(() => {
    if (storedId !== null && !isMember) {
      setActiveTeamId(null);
    }
  }, [storedId, isMember]);

  const activeTeamId = isMember ? storedId : null;
  const activeTeam = activeTeamId
    ? teams.find((t) => t.id === activeTeamId) ?? null
    : null;

  return {
    teams,
    activeTeamId,
    activeTeam,
    setActiveTeam: setActiveTeamId,
  };
}
